import { useEffect, useId, useRef, useState } from "react";

import { MODULE_NAV_ICON_KEYS, moduleNavIcon } from "@/components/moduleNavIcons";
import { useTouchListPickSession } from "@/lib/useTouchListPickSession";
import { cn } from "@/lib/utils";

export function ModuleNavIconPicker({
  value,
  used,
  onChange,
}: {
  value: string;
  used: ReadonlySet<string>;
  onChange: (icon: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const CurrentIcon = moduleNavIcon(value);
  const { onListItemPointerDown, shouldYieldBlurCommit } = useTouchListPickSession(open);
  const yieldBlurRef = useRef(shouldYieldBlurCommit);
  yieldBlurRef.current = shouldYieldBlurCommit;

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent): void {
      if (yieldBlurRef.current()) return;
      if (rootRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-label="Choose icon"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => {
          setSearch("");
          setOpen((current) => !current);
        }}
        className="grid h-10 w-10 place-items-center rounded-md border border-slate-300 text-brand-700"
      >
        <CurrentIcon className="h-5 w-5" aria-hidden />
      </button>
      {open ? (
        <div
          id={listId}
          role="listbox"
          aria-label="Group icons"
          className="absolute left-0 z-20 mt-1 w-72 rounded-lg border border-slate-200 bg-white p-2 shadow-xl"
        >
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search icons"
            aria-label="Search icons"
            className="mb-2 h-9 w-full rounded-md border border-slate-300 px-2 text-sm"
          />
          <div className="grid max-h-64 grid-cols-4 gap-1 overflow-y-auto">
          {MODULE_NAV_ICON_KEYS.filter((name) => name.includes(search.trim().toLowerCase())).map((name) => {
            const OptionIcon = moduleNavIcon(name);
            const taken = used.has(name) && name !== value;
            const selected = name === value;
            return (
              <button
                key={name}
                type="button"
                role="option"
                aria-selected={selected}
                aria-label={name}
                disabled={taken}
                onPointerDown={(event) => {
                  if (taken) return;
                  onListItemPointerDown(event, () => {
                    onChange(name);
                    setOpen(false);
                  });
                }}
                className={cn(
                  "grid h-12 place-items-center rounded-md",
                  selected ? "bg-brand-50 text-brand-700" : "text-slate-700 hover:bg-slate-100",
                  "disabled:cursor-not-allowed disabled:opacity-25",
                )}
              >
                <OptionIcon className="h-5 w-5" aria-hidden />
              </button>
            );
          })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
